import {
  getPageHeader,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  Badge,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@yeshuman/sdk'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { HealthListPageSkeleton } from './health-list-page-skeleton'
import { CalendarCheck, Trash2 } from 'lucide-react'
import { useConsultReports, useDeleteConsultReport } from '../hooks/use-consult-reports'
import { toast } from 'sonner'

export function ConsultationsPage() {
  const { title, description } = getPageHeader(
    '/consultations',
    'Consultations',
    'Prepare and share consultation prep reports.',
  )
  const { reports, isLoading, error } = useConsultReports()
  const deleteReport = useDeleteConsultReport()
  const [deletingId, setDeletingId] = useState<string | null>(null)

  const handleDelete = async (id: string) => {
    try {
      await deleteReport.mutateAsync(id)
      toast.success('Consultation report deleted')
      setDeletingId(null)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete')
    }
  }

  return (
    <PageContainer>
      <PageHeader
        leading={<PageHeaderLeading />}
        title={title}
        description={description}
        icon={<CalendarCheck className="h-5 w-5" />}
      />

      {isLoading ? <HealthListPageSkeleton /> : null}

      {error ? (
        <Card>
          <CardHeader>
            <CardTitle>Could not load reports</CardTitle>
            <CardDescription>
              {error instanceof Error ? error.message : 'Something went wrong'}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {!isLoading && !error && reports.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No consultation prep reports yet</CardTitle>
            <CardDescription>
              Ask Lumie to prepare a report for your visit. For example: “Prepare a consultation
              report for my upcoming GP visit about sleep and energy.”
            </CardDescription>
          </CardHeader>
        </Card>
      ) : null}

      {!isLoading && reports.length > 0 ? (
        <div className="space-y-3">
          {reports.map((report) => (
            <Card key={report.id} className="transition-colors hover:bg-muted/30">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-3">
                  <Link to={`/consultations/${report.id}`} className="min-w-0 flex-1">
                    <CardTitle className="text-base">
                      {report.payload?.headline || report.visit_label || 'Consultation prep'}
                    </CardTitle>
                  </Link>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge variant={report.status === 'ready' ? 'successSoft' : 'secondary'}>
                      {report.status}
                    </Badge>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      aria-label="Delete consultation report"
                      onClick={() => setDeletingId(report.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <Link to={`/consultations/${report.id}`}>
                  <CardDescription className="line-clamp-2">{report.intent_text}</CardDescription>
                </Link>
              </CardHeader>
              <CardContent className="text-xs text-muted-foreground">
                {new Date(report.created_at).toLocaleString()}
                {report.share_active ? ' · Share link active' : null}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : null}

      <AlertDialog open={deletingId !== null} onOpenChange={(open) => !open && setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this consultation report?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the prep pack and any active share link. You cannot undo
              this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deletingId && void handleDelete(deletingId)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  )
}
